import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

export type ChartTick = { ts: string; yes_price_cents: number };

type Props = {
  ticks: ChartTick[];
  compact?: boolean;
  showNo?: boolean;
  live?: boolean;
  fallbackCents?: number;
};

function labelFor(ts: string, compact: boolean): string {
  const d = new Date(ts);
  if (compact) {
    return d.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric" });
}

export function PriceChart({ ticks, compact = false, showNo = false, live = false, fallbackCents }: Props) {
  const source = ticks.length
    ? ticks
    : fallbackCents != null
      ? [
          { ts: new Date(Date.now() - 3600_000).toISOString(), yes_price_cents: fallbackCents },
          { ts: new Date().toISOString(), yes_price_cents: fallbackCents },
        ]
      : [];
  const data = source.map((t) => ({
    t: labelFor(t.ts, compact),
    yes: t.yes_price_cents,
    no: Math.max(0, 100 - t.yes_price_cents),
  }));
  if (!data.length) {
    return (
      <div className={`chart-box${compact ? " compact" : ""}`} style={{ display: "grid", placeItems: "center", color: "var(--muted)" }}>
        Probability history will plot after the first tick.
      </div>
    );
  }
  const values = data.flatMap((d) => (showNo ? [d.yes, d.no] : [d.yes]));
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const pad = compact ? 6 : 0;
  const domain: [number, number] = compact
    ? [Math.max(0, Math.floor(lo - pad)), Math.min(100, Math.ceil(hi + pad))]
    : [0, 100];
  if (domain[1] - domain[0] < 8) {
    domain[0] = Math.max(0, domain[0] - 4);
    domain[1] = Math.min(100, domain[1] + 4);
  }

  return (
    <div className={`chart-box${compact ? " compact" : ""}`} role="img" aria-label="Probability chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <XAxis dataKey="t" hide={!compact} tick={{ fontSize: 11, fill: "var(--muted)" }} minTickGap={28} />
          <YAxis domain={domain} width={36} tick={{ fontSize: 11, fill: "var(--muted)" }} tickFormatter={(v) => `${v}%`} />
          <Tooltip
            formatter={(value) => [`${value}%`]}
            contentStyle={{ borderRadius: 12, border: "1px solid var(--border)", background: "var(--surface)" }}
          />
          <Line type="monotone" dataKey="yes" stroke="#16a34a" strokeWidth={2.2} dot={false} name="Yes" />
          {showNo && <Line type="monotone" dataKey="no" stroke="#ea580c" strokeWidth={2.2} dot={false} name="No" />}
        </LineChart>
      </ResponsiveContainer>
      {live && (
        <span className="chart-live">
          <i /> LIVE
        </span>
      )}
    </div>
  );
}
